/**
 * Shared helper functions for building change columns in AG Grid
 * Used by both initial grid setup and dynamic change updates
 *
 * Column model:
 *   - Every change gets exactly ONE column group (keyed by change.id).
 *   - If 2+ changes share the same parent_change label they are wrapped inside
 *     a collapsible parent column group.  A single change with a parent_change
 *     renders standalone until a sibling arrives.
 *   - Changes are NEVER merged into a single column regardless of label.
 */

import { getMonthsBetweenDates, numberWithCommas } from './utils';
import {
  currencyFormatter,
  getSingleMonthChange,
  changeTotal,
  forecastPeriodMonth,
  forecastPeriodTotal,
} from './aggrid';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const isCalculatedAccount = (change) =>
  change.function_operator === 'calculatedAccount' ||
  change.function_operator === 'recursiveCalculatedAccount';

/**
 * Standard aggregation function used on every change column group.
 * Sums non-null child values; returns null (not 0) when nothing applies so
 * AG Grid does not render a zero in rows that are unaffected by this change.
 */
const sumAggFunc = (params) => {
  let sum = 0;
  let hasValue = false;
  params.values.forEach((v) => {
    if (v !== null && v !== undefined && v !== '') {
      sum += Number(v);
      hasValue = true;
    }
  });
  return hasValue ? sum : null;
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Removes change columns for the given changes from the columns array.
 * Handles both top-level change columns and columns nested inside a parent
 * change group.
 *
 * @param {Array} columns  - Columns array (mutated in place)
 * @param {Array} changes  - Changes whose columns should be removed
 */
export const removeOldChangeColumns = (columns, changes) => {
  if (!changes || !Array.isArray(changes)) return;

  const idsToRemove = new Set(
    changes
      .filter((c) => c && Object.keys(c).length > 0)
      .map((c) => String(c.id))
  );

  if (idsToRemove.size === 0) return;

  for (let i = columns.length - 1; i >= 0; i--) {
    const col = columns[i];

    if (col.children) {
      // Remove matching direct children (individual change groups inside a parent wrapper)
      col.children = col.children.filter(
        (child) => !idsToRemove.has(String(child.metadata?.changeId))
      );

      // If only the Total leaf remains (no actual change children), remove the whole group.
      // The Total leaf has no metadata.changeId and its colId ends with '__total'.
      const changeChildren = col.children.filter((c) => c.metadata?.changeId != null);
      if (changeChildren.length === 0) {
        columns.splice(i, 1);
      }
    } else if (idsToRemove.has(String(col.metadata?.changeId))) {
      columns.splice(i, 1);
    }
  }
};

/**
 * Rebuilds the forecast column with updated changes.
 *
 * The valueGetters close over the full `changes` array AND stable React refs.
 * At render time each valueGetter reads `.current` so toggling visibility or
 * changing the date filter triggers a `refreshCells` — not a full colDef
 * rebuild — to update the forecast numbers.
 *
 * @param {Array}   columns              - Columns array (mutated in place)
 * @param {Array}   changes              - All changes in the project
 * @param {Object}  projectInfo          - Project information with start_date and end_date
 * @param {Object}  [disabledChangeIdsRef]   - React ref whose `.current` is a Set of disabled change IDs.
 * @param {Object}  [effectiveDateRangeRef]  - React ref whose `.current` is the active date range filter
 *                                            { from: {year, month}, to: {year, month} } or null.
 */
export const rebuildForecastColumn = (columns, changes, projectInfo, disabledChangeIdsRef = null, effectiveDateRangeRef = null) => {
  if (!projectInfo?.start_date || !projectInfo?.end_date) {
    console.warn('rebuildForecastColumn: Missing project info');
    return;
  }

  const forecastIndex = columns.findIndex((col) => col.field === 'forecastPeriod');
  if (forecastIndex > -1) columns.splice(forecastIndex, 1);

  const forecastDates = getMonthsBetweenDates(projectInfo.start_date, projectInfo.end_date);

  // Returns only the changes that are not currently disabled.
  // Reading from the ref at call time (not at closure-creation time) means the
  // same valueGetter function always reflects the latest toggle state.
  const getActiveChanges = () => {
    if (!disabledChangeIdsRef?.current?.size) return changes;
    return changes.filter((c) => !disabledChangeIdsRef.current.has(c.id));
  };

  const forecastPeriodMonths = forecastDates.map((month) => ({
    aggFunc: 'sum',
    columnGroupShow: 'open',
    field: `${month} forecast`,
    headerName: month,
    resizable: true,
    type: 'numericRight',
    valueFormatter: currencyFormatter,
    valueGetter: (params) => forecastPeriodMonth(month, params, getActiveChanges()),
  }));

  forecastPeriodMonths.push({
    aggFunc: 'sum',
    columnGroupShow: 'closed',
    field: 'forecast total',
    headerName: 'Total',
    pinned: 'right',
    resizable: true,
    type: 'numericRight',
    valueFormatter: currencyFormatter,
    valueGetter: (params) => forecastPeriodTotal(params, getActiveChanges(), effectiveDateRangeRef?.current ?? null),
  });

  columns.push({
    children: forecastPeriodMonths,
    field: 'forecastPeriod',
    filter: true,
    groupId: 'forecastPeriod',
    headerName: 'Forecast Period',
    marryChildren: true,
    openByDefault: false,
    resizable: true,
    type: 'numericRight',
  });
};

/**
 * Builds one AG Grid column group definition per change.
 * Calculated accounts are filtered out — they are rows, not columns.
 *
 * @param {Array}  changes                  - Changes to process
 * @param {Object} _activeProjectAttributes - Unused; kept for call-site compat
 * @param {Object} [disabledChangeIdsRef]   - React ref whose `.current` is a Set of disabled change IDs.
 * @param {Object} [effectiveDateRangeRef]  - React ref whose `.current` is the active date range filter or null.
 * @returns {Array} One colDef group object per change
 */
export const processChangeColumns = (changes, _activeProjectAttributes, disabledChangeIdsRef = null, effectiveDateRangeRef = null) => {
  if (!changes || changes.length === 0) return [];

  const changeColumnGroups = [];

  changes.forEach((change) => {
    if (!change || Object.keys(change).length === 0) return;
    if (isCalculatedAccount(change)) return;

    // Guard against partially-loaded change records (e.g. early-navigation polling
    // returns the change before the backend has set date_range or change_label).
    // Passing undefined as headerName to AG Grid causes it to call .length on
    // undefined inside its column-group header renderer — the exact production crash.
    if (!change.date_range || !Array.isArray(change.date_range) || change.date_range.length < 2) {
      console.warn('[processChangeColumns] Skipping change with missing date_range:', change.id, change.change_label);
      return;
    }

    const label = change.change_label ?? `Change ${change.id}`;
    const dates = getMonthsBetweenDates(change.date_range[0], change.date_range[1]) ?? [];

    if (dates.length === 0) {
      console.warn('[processChangeColumns] Skipping change with unparseable date_range:', change.id, change.date_range);
      return;
    }

    // Monthly leaf columns — one per month in this change's date range
    const columnMonths = dates.map((month) => ({
      aggFunc: sumAggFunc,
      changeId: change.id,
      colId: `${change.id} ${month}`,
      columnGroupShow: 'open',
      field: `${change.id} ${month}`,
      headerName: month,
      headerTooltip: `${label} - ${month}`,
      resizable: true,
      type: 'numericRight',
      valueFormatter: currencyFormatter,
      valueGetter: (params) => {
        if (!params.data?.id) return null;
        const value = getSingleMonthChange(month, params, change);
        return value !== '' && value !== null && value !== undefined ? value : null;
      },
      metadata: { changeId: change.id, parent_change: change.parent_change || null },
    }));

    // Collapsed total column
    columnMonths.push({
      aggFunc: sumAggFunc,
      changeId: change.id,
      colId: `${change.id} total`,
      columnGroupShow: 'closed',
      field: `${change.id} total`,
      headerName: 'Total',
      headerTooltip: `${label} - Total`,
      resizable: true,
      type: ['numericRight', 'changeTotal'],
      valueFormatter: currencyFormatter,
      valueGetter: (params) => {
        if (!params.data?.id) return null;
        const total = changeTotal(params, change, effectiveDateRangeRef?.current ?? null);
        return total !== '' && total !== null && total !== undefined ? total : null;
      },
      metadata: { changeId: change.id, parent_change: change.parent_change || null },
    });

    // The change column group — tooltip is on the group header via ChangeColumnHeader component only
    changeColumnGroups.push({
      aggFunc: sumAggFunc,
      changeId: change.id,
      children: columnMonths,
      colId: String(change.id),
      columnGroupShow: 'open',
      field: String(change.id),
      groupId: String(change.id),
      headerName: label,
      headerGroupComponent: 'changeColumnHeader',
      marryChildren: true,
      openByDefault: false,
      resizable: true,
      type: 'numericRight',
      valueFormatter: currencyFormatter,
      metadata: {
        changeId: change.id,
        parent_change: change.parent_change || null,
        isDominant: change.is_dominant_change ?? false,
      },
    });
  });

  return changeColumnGroups;
};

/**
 * Inserts change column groups into the columns array, wrapping siblings that
 * share the same parent_change label into a collapsible parent column group.
 *
 * Rules:
 *   - No parent_change  → insert standalone before Forecast Period
 *   - parent_change, only 1 sibling so far → insert standalone (no wrapper yet)
 *   - parent_change, 2+ siblings → wrap all of them in a parent group
 *
 * @param {Array}  columns                 - Columns array (mutated in place)
 * @param {Array}  changeColumnGroups      - Output of processChangeColumns
 * @param {Object} activeProjectAttributes - Must include parent_changes array
 */
export const insertChangeColumnGroups = (columns, changeColumnGroups, activeProjectAttributes, effectiveDateRangeRef = null) => {
  const toInsertStandalone = [];

  changeColumnGroups.forEach((changeCol) => {
    const parentChangeLabel = changeCol.metadata?.parent_change;

    if (!parentChangeLabel) {
      toInsertStandalone.push(changeCol);
      return;
    }

    const parentEntry = activeProjectAttributes.parent_changes?.find(
      (pc) => pc.label === parentChangeLabel
    );
    const siblingCount = parentEntry?.changes?.length ?? 0;

    if (siblingCount < 2) {
      toInsertStandalone.push(changeCol);
      return;
    }

    // 2+ siblings — find or create the parent wrapper group
    const existingGroupIndex = columns.findIndex(
      (col) => col.field === parentChangeLabel && col.children
    );

    // Also check toInsertStandalone — the parent group may have been created
    // while processing an earlier sibling in this same forEach pass
    const pendingGroupIndex = toInsertStandalone.findIndex(
      (col) => col.field === parentChangeLabel && col.children
    );

    if (existingGroupIndex > -1) {
      const group = columns[existingGroupIndex];
      const alreadyIn = group.children.some(
        (c) => c.metadata?.changeId === changeCol.metadata.changeId
      );
      if (!alreadyIn) {
        group.children.push(_markAsParentGroupChild(changeCol));
      }
    } else if (pendingGroupIndex > -1) {
      const group = toInsertStandalone[pendingGroupIndex];
      const alreadyIn = group.children.some(
        (c) => c.metadata?.changeId === changeCol.metadata.changeId
      );
      if (!alreadyIn) {
        group.children.push(_markAsParentGroupChild(changeCol));
      }
    } else {
      const parentGroup = _buildParentGroup(parentChangeLabel);

      // Sweep standalone siblings already queued
      for (let i = toInsertStandalone.length - 1; i >= 0; i--) {
        if (toInsertStandalone[i].metadata?.parent_change === parentChangeLabel) {
          parentGroup.children.push(_markAsParentGroupChild(toInsertStandalone.splice(i, 1)[0]));
        }
      }

      // Sweep standalone siblings already inserted into columns
      for (let i = columns.length - 1; i >= 0; i--) {
        if (
          columns[i].metadata?.parent_change === parentChangeLabel &&
          !columns[i].children
        ) {
          parentGroup.children.push(_markAsParentGroupChild(columns.splice(i, 1)[0]));
        }
      }

      parentGroup.children.push(_markAsParentGroupChild(changeCol));
      toInsertStandalone.push(parentGroup);
    }
  });

  // Finalise all parent group Total valueGetters now that children are known.
  // Only run on parent wrapper groups (no metadata.changeId) not individual change groups.
  const isParentWrapper = (col) => col.groupId && col.children && !col.metadata?.changeId;

  toInsertStandalone.forEach((col) => {
    if (isParentWrapper(col)) _finaliseParentGroup(col, effectiveDateRangeRef);
  });
  columns.forEach((col) => {
    if (isParentWrapper(col)) _finaliseParentGroup(col, effectiveDateRangeRef);
  });

  // Insert all standalone columns (and parent group wrappers) before Forecast Period
  if (toInsertStandalone.length > 0) {
    const forecastIndex = columns.findIndex(
      (col) => col.field === 'forecastPeriod' || col.groupId === 'forecastPeriod'
    );
    if (forecastIndex !== -1) {
      columns.splice(forecastIndex, 0, ...toInsertStandalone);
    } else {
      const basePeriodIndex = columns.findIndex((col) => col.field === 'basePeriod');
      if (basePeriodIndex !== -1) {
        columns.splice(basePeriodIndex + 1, 0, ...toInsertStandalone);
      } else {
        columns.push(...toInsertStandalone);
      }
    }
  }
};

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Builds the outer parent change column group wrapper.
 * Includes a collapsed Total leaf. Call _finaliseParentGroup() after all
 * children have been added to wire up the Total valueGetter.
 */
const _buildParentGroup = (parentChangeLabel) => ({
  aggFunc: sumAggFunc,
  children: [],
  field: parentChangeLabel,
  groupId: parentChangeLabel,
  headerGroupComponent: 'parentChangeGroupHeader',
  headerName: parentChangeLabel,
  marryChildren: true,
  openByDefault: false,
  resizable: true,
  type: 'numericRight',
  valueFormatter: currencyFormatter,
});

/**
 * After all child change groups have been added to a parent group, wire up
 * the collapsed Total leaf's valueGetter to sum each child's total field.
 * Mutates the parent group in place.
 */
const _finaliseParentGroup = (parentGroup, effectiveDateRangeRef = null) => {
  // Collect child change groups with their ids and labels
  const childChanges = parentGroup.children
    .filter((c) => c.metadata?.changeId != null && Array.isArray(c.children))
    .map((c) => ({ id: c.metadata.changeId, label: c.headerName, isDominant: c.metadata?.isDominant ?? false }));

  if (childChanges.length === 0) return;

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const totalLeaf = {
    aggFunc: sumAggFunc,
    colId: `${parentGroup.field}__total`,
    columnGroupShow: 'closed',
    field: `${parentGroup.field}__total`,
    headerName: 'Total',
    resizable: true,
    type: 'numericRight',
    valueFormatter: (params) => {
      if (params.value === null || params.value === undefined || params.value === '') return '';
      const raw = Number(params.value);
      if (isNaN(raw)) return '';
      const rounded = Math.round(raw);
      if (rounded === 0) return '0';
      return numberWithCommas(rounded);
    },
    valueGetter: (params) => {
      if (!params.data?.id) return null;
      const activeDateRange = effectiveDateRangeRef?.current ?? null;

      let sum = 0;
      let isTouched = false;

      if (activeDateRange) {
        // Date-range-filtered: sum per-month fields across filtered months only.
        // Each child uses id-keyed fields for dominant changes, label-keyed for others.
        const fromNum = activeDateRange.from.year * 100 + activeDateRange.from.month;
        const toNum   = activeDateRange.to.year   * 100 + activeDateRange.to.month;

        childChanges.forEach(({ id, label, isDominant }) => {
          const prefix = isDominant ? String(id) : label;
          Object.keys(params.data).forEach(key => {
            if (!key.startsWith(prefix + ' ')) return;
            const monthPart = key.slice(prefix.length + 1);
            const m = monthPart.match(/^([A-Za-z]{3}) (\d{4})$/);
            if (!m) return;
            const idx = monthNames.indexOf(m[1]);
            if (idx === -1) return;
            const n = Number(m[2]) * 100 + (idx + 1);
            if (n < fromNum || n > toNum) return;
            const v = Number(params.data[key]) || 0;
            if (v !== 0) isTouched = true;
            sum += v;
          });
        });
      } else {
        // Unfiltered: use pre-stored totals (fast path)
        childChanges.forEach(({ id, label }) => {
          const idKey    = `${id} total`;
          const labelKey = `${label} total`;
          const v = params.data[idKey] !== undefined && params.data[idKey] !== null
            ? params.data[idKey]
            : params.data[labelKey];
          if (v !== null && v !== undefined && v !== '') {
            isTouched = true;
            sum += Number(v);
          }
        });
      }

      if (!isTouched) return null;
      return sum;
    },
  };

  // Insert Total leaf at position 0 (before the child change groups)
  parentGroup.children.unshift(totalLeaf);
};

/**
 * Marks a change column group as a nested child of a parent group.
 * Sets columnGroupShow: 'open' so it only appears when the parent is expanded.
 * Mutates the column object in place.
 */
const _markAsParentGroupChild = (changeCol) => {
  changeCol.columnGroupShow = 'open';
  return changeCol;
};
